import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  BrandingWatermark as BrandIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Timeline as TimelineIcon,
  Visibility,
  Edit,
  Close,
  Save,
  Add,
  Settings,
  TrendingUp,
  Power as PowerIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';

const Brand = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    searchTerm: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusConfirmDialogOpen, setStatusConfirmDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedForStatus, setSelectedForStatus] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editFormData, setEditFormData] = useState({
    brandName: '',
    manufacturer: '',
    country: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    isActive: true,
    establishedYear: '',
    specialization: '',
    certifications: '',
  });

  const sampleData = [
    { 
      id: 1, 
      brandName: 'SunPower', 
      manufacturer: 'SunPower Corporation',
      country: 'USA',
      website: 'www.sunpower.com',
      email: 'info@sunpower.com',
      phone: '+1-408-240-5500',
      address: 'San Jose, California, USA',
      description: 'Leading solar panel manufacturer specializing in high-efficiency panels',
      isActive: true,
      establishedYear: '1985',
      specialization: 'Solar Panels',
      certifications: 'ISO 9001, IEC 61215',
      totalProducts: 45,
      activeProducts: 38,
      marketShare: 12.5,
    },
    { 
      id: 2, 
      brandName: 'Tesla Energy', 
      manufacturer: 'Tesla, Inc.',
      country: 'USA',
      website: 'www.tesla.com/energy',
      email: 'energy@tesla.com',
      phone: '+1-650-681-5000',
      address: 'Austin, Texas, USA',
      description: 'Innovative energy storage and solar solutions',
      isActive: true,
      establishedYear: '2003',
      specialization: 'Battery Storage & Solar',
      certifications: 'UL, CE, CSA',
      totalProducts: 28,
      activeProducts: 25,
      marketShare: 8.2,
    },
    { 
      id: 3, 
      brandName: 'Vestas', 
      manufacturer: 'Vestas Wind Systems',
      country: 'Denmark',
      website: 'www.vestas.com',
      email: 'info@vestas.com',
      phone: '+45-97-30-00-00',
      address: 'Aarhus, Denmark',
      description: 'World leader in wind energy solutions',
      isActive: true,
      establishedYear: '1945',
      specialization: 'Wind Turbines',
      certifications: 'ISO 9001, CE, IEC',
      totalProducts: 52,
      activeProducts: 48,
      marketShare: 15.8,
    },
    { 
      id: 4, 
      brandName: 'Canadian Solar', 
      manufacturer: 'Canadian Solar Inc.',
      country: 'Canada',
      website: 'www.cansolar.com',
      email: 'info@cansolar.com',
      phone: '+1-905-530-2200',
      address: 'Guelph, Ontario, Canada',
      description: 'Global provider of solar energy solutions',
      isActive: true,
      establishedYear: '2001',
      specialization: 'Solar Panels',
      certifications: 'ISO 9001, IEC 61215',
      totalProducts: 62,
      activeProducts: 55,
      marketShare: 10.3,
    },
    { 
      id: 5, 
      brandName: 'LG Solar', 
      manufacturer: 'LG Electronics',
      country: 'South Korea',
      website: 'www.lg.com/us/solar',
      email: 'solar@lg.com',
      phone: '+82-2-3777-1114',
      address: 'Seoul, South Korea',
      description: 'Premium solar panels with high efficiency',
      isActive: false,
      establishedYear: '1958',
      specialization: 'Solar Panels',
      certifications: 'ISO 9001, IEC 61215',
      totalProducts: 35,
      activeProducts: 20,
      marketShare: 6.5,
    },
    { 
      id: 6, 
      brandName: 'SMA Solar', 
      manufacturer: 'SMA Solar Technology',
      country: 'Germany',
      website: 'www.sma.de',
      email: 'info@sma.de',
      phone: '+49-561-9522-0',
      address: 'Niestetal, Germany',
      description: 'Leading inverter manufacturer',
      isActive: true,
      establishedYear: '1981',
      specialization: 'Solar Inverters',
      certifications: 'CE, VDE, IEC',
      totalProducts: 42,
      activeProducts: 40,
      marketShare: 9.1,
    },
  ];

  const [data, setData] = useState(sampleData);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    // Determine if we're creating or updating
    if (selectedBrand) {
      handleUpdateBrand();
    } else {
      handleCreateBrand();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  // New Brand handler
  const handleNewBrand = () => {
    console.log('Creating new brand...');
    // Reset form data for new brand
    setEditFormData({
      brandName: '',
      manufacturer: '',
      country: '',
      website: '',
      email: '',
      phone: '',
      address: '',
      description: '',
      isActive: true,
      establishedYear: '',
      specialization: '',
      certifications: '',
    });
    setSelectedBrand(null);
    setEditDialogOpen(true);
  };

  // Create new brand
  const handleCreateBrand = () => {
    if (!editFormData.brandName || !editFormData.manufacturer) {
      alert('Please fill in required fields: Brand Name and Manufacturer');
      return;
    }

    // Create new brand object
    const newBrand = {
      id: Date.now(),
      brandName: editFormData.brandName,
      manufacturer: editFormData.manufacturer,
      country: editFormData.country,
      website: editFormData.website,
      email: editFormData.email,
      phone: editFormData.phone,
      address: editFormData.address,
      description: editFormData.description,
      isActive: editFormData.isActive,
      establishedYear: editFormData.establishedYear,
      specialization: editFormData.specialization,
      certifications: editFormData.certifications,
      totalProducts: 0,
      activeProducts: 0,
      marketShare: 0,
    };

    console.log('Creating new brand:', newBrand);
    setData(prev => [newBrand, ...prev]);
    alert(`New brand "${newBrand.brandName}" created successfully!`);
    
    // Close dialog and reset form
    setEditDialogOpen(false);
    setSelectedBrand(null);
    setEditFormData({
      brandName: '',
      manufacturer: '',
      country: '',
      website: '',
      email: '',
      phone: '',
      address: '',
      description: '',
      isActive: true,
      establishedYear: '',
      specialization: '',
      certifications: '',
    });
  };

  // Update existing brand
  const handleUpdateBrand = () => {
    if (!editFormData.brandName || !editFormData.manufacturer) {
      alert('Please fill in required fields: Brand Name and Manufacturer');
      return;
    }

    const updatedBrand = {
      ...selectedBrand,
      brandName: editFormData.brandName,
      manufacturer: editFormData.manufacturer,
      country: editFormData.country,
      website: editFormData.website,
      email: editFormData.email,
      phone: editFormData.phone,
      address: editFormData.address,
      description: editFormData.description,
      isActive: editFormData.isActive,
      establishedYear: editFormData.establishedYear,
      specialization: editFormData.specialization,
      certifications: editFormData.certifications,
    };

    console.log('Updating brand:', updatedBrand);
    setData(prev => prev.map(i => i.id === updatedBrand.id ? updatedBrand : i));
    alert(`Brand "${updatedBrand.brandName}" updated successfully!`);
    
    // Close dialog and reset form
    setEditDialogOpen(false);
    setSelectedBrand(null);
    setEditFormData({
      brandName: '',
      manufacturer: '',
      country: '',
      website: '',
      email: '',
      phone: '',
      address: '',
      description: '',
      isActive: true,
      establishedYear: '',
      specialization: '',
      certifications: '',
    });
  };

  // Search handler - applies all filters
  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    console.log('Search triggered with filters:', {
      searchTerm: searchTerm,
      status: filters.status,
    });
    // Simulate API call delay
    setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm: searchTerm }));
      setSearchLoading(false);
    }, 500);
  };

  // View and Edit action handlers
  const handleView = (brand) => {
    setSelectedBrand(brand);
    setViewDialogOpen(true);
  };

  const handleEdit = (brand) => {
    setSelectedBrand(brand);
    setEditFormData({
      brandName: brand.brandName,
      manufacturer: brand.manufacturer,
      country: brand.country,
      website: brand.website,
      email: brand.email,
      phone: brand.phone,
      address: brand.address,
      description: brand.description,
      isActive: brand.isActive,
      establishedYear: brand.establishedYear,
      specialization: brand.specialization,
      certifications: brand.certifications,
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedBrand(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedBrand(null);
    setEditFormData({
      brandName: '',
      manufacturer: '',
      country: '',
      website: '',
      email: '',
      phone: '',
      address: '',
      description: '',
      isActive: true,
      establishedYear: '',
      specialization: '',
      certifications: '',
    });
  };

  // Optimized filter function with memoization - only works when search is performed
  const getFilteredData = useCallback(() => {
    // If no search has been performed, return all data
    if (!hasSearched) {
      return data;
    }

    let filteredData = [...data];

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.brandName.toLowerCase().includes(searchLower) ||
        item.manufacturer.toLowerCase().includes(searchLower) ||
        item.country.toLowerCase().includes(searchLower) ||
        item.specialization.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status) {
      filteredData = filteredData.filter(item => 
        filters.status === 'active' ? item.isActive : !item.isActive
      );
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.status, data]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalBrands = filteredData.length;
  const activeBrands = filteredData.filter(item => item.isActive).length;
  const totalProducts = filteredData.reduce((sum, item) => sum + item.totalProducts, 0);
  const averageMarketShare = filteredData.length > 0 ? 
    (filteredData.reduce((sum, item) => sum + item.marketShare, 0) / filteredData.length).toFixed(1) : 0;

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase();
  };

  const handleStatusToggle = (item) => {
    setSelectedForStatus(item);
    setStatusConfirmDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    if (!selectedForStatus) return;
    setData(prev => prev.map(i => i.id === selectedForStatus.id ? { ...i, isActive: !i.isActive } : i));
    setStatusConfirmDialogOpen(false);
    setSelectedForStatus(null);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Green Energy Brand Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage renewable energy equipment brands and manufacturers
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleNewBrand}
            sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            New Brand
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <BrandIcon sx={{ fontSize: 28, color: '#536DFE', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {totalBrands}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Brands
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {activeBrands}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Active Brands
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <PowerIcon sx={{ fontSize: 28, color: '#ff9800', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {totalProducts}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Products
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <TrendingUp sx={{ fontSize: 28, color: '#2196f3', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {averageMarketShare}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Avg Market Share
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
            Search & Filters
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{
                minWidth: 300,
                maxWidth: 400,
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            
            {/* Filter Controls */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Brand Status</InputLabel>
              <Select 
                label="Brand Status" 
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                }}
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={searchLoading}
              startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  backgroundColor: '#3F51B5',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilters({
                  status: '',
                  searchTerm: '',
                });
                setSearchTerm('');
                setHasSearched(false);
                setSearchLoading(false);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#536DFE',
                color: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#3F51B5',
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              Clear All
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog 
        open={statusConfirmDialogOpen} 
        onClose={() => setStatusConfirmDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white', display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {selectedForStatus?.isActive ? 'Deactivate Brand' : 'Activate Brand'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to {selectedForStatus?.isActive ? 'deactivate' : 'activate'} the brand <strong>"{selectedForStatus?.brandName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedForStatus?.isActive
              ? 'Deactivating this brand will mark it as inactive in the system.'
              : 'Activating this brand will make it active in the system.'
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setStatusConfirmDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}>Cancel</Button>
          <Button onClick={confirmStatusToggle} variant="contained" color={selectedForStatus?.isActive ? 'warning' : 'success'} sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}>
            {selectedForStatus?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Search Results Summary */}
      {hasSearched && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '1px solid #e8f5e8' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1,
                backgroundColor: '#e8f5e8',
                borderRadius: 2,
                minWidth: 'fit-content'
              }}>
                {searchLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SearchIcon sx={{ color: '#536DFE' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#536DFE' }}>
                  Search Results
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Searching brands...
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                      {filteredData.length} brand{filteredData.length !== 1 ? 's' : ''} found
                    </Typography>
                    {filters.status && filters.status !== 'All Status' && (
                      <Chip
                        label={`Status: ${filters.status}`} 
                        size="small"
                        color="success" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Brand List
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Brand</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Manufacturer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Country</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Specialization</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Products</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Market Share</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Searching brands...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow 
                      key={row.id} 
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#e8f5e8 !important' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: '#536DFE', width: 32, height: 32 }}>
                            {getInitials(row.brandName)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.brandName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Est. {row.establishedYear}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.manufacturer}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.country}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.specialization}
                          size="small" 
                          sx={{ 
                            backgroundColor: '#536DFE',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.activeProducts}/{row.totalProducts}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Active/Total
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${row.marketShare}%`}
                          size="small" 
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={row.isActive}
                              onChange={() => handleStatusToggle(row)}
                              color="success"
                              size="medium"
                            />
                          }
                          label={
                            <Chip
                              label={row.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={getStatusColor(row.isActive)}
                              sx={{ fontWeight: 600 }}
                            />
                          }
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleView(row)}
                              sx={{ color: '#536DFE' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Brand">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEdit(row)}
                              sx={{ color: '#ff9800' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No brands found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BrandIcon />
            <Typography variant="h6" fontWeight="bold">
              Brand Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedBrand && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 64, height: 64, fontSize: '24px' }}>
                  {getInitials(selectedBrand.brandName)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedBrand.brandName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedBrand.manufacturer}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={selectedBrand.isActive ? 'Active' : 'Inactive'} 
                      size="small" 
                      color={getStatusColor(selectedBrand.isActive)}
                    />
                    <Chip 
                      label={`${selectedBrand.marketShare}% Market Share`} 
                      size="small" 
                      color="primary"
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Brand Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon color="primary" />
                        Brand Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Brand Name:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBrand.brandName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Manufacturer:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBrand.manufacturer}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Country:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBrand.country}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Established:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBrand.establishedYear}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Specialization:</Typography>
                          <Chip 
                            label={selectedBrand.specialization}
                            size="small" 
                            sx={{ 
                              backgroundColor: '#536DFE',
                              color: 'white'
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Description:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBrand.description}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Contact Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon color="primary" />
                        Contact Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon sx={{ fontSize: 18, color: '#666' }} />
                          <Typography variant="body2" fontWeight="600">{selectedBrand.address}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 18, color: '#666' }} />
                          <Typography variant="body2" fontWeight="600">{selectedBrand.phone}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 18, color: '#666' }} />
                          <Typography variant="body2" fontWeight="600">{selectedBrand.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WebsiteIcon sx={{ fontSize: 18, color: '#666' }} />
                          <Typography variant="body2" fontWeight="600">{selectedBrand.website}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">Certifications:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBrand.certifications}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Performance Metrics */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp color="primary" />
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="bold" color="primary">
                          {selectedBrand.totalProducts}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Products
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                          {selectedBrand.activeProducts}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active Products
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="bold" color="warning.main">
                          {selectedBrand.marketShare}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Market Share
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Brand Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: selectedBrand ? '#ff9800' : '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedBrand ? <Edit /> : <Add />}
            <Typography variant="h6" fontWeight="bold">
              {selectedBrand ? 'Edit Brand' : 'New Brand'}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEditDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {selectedBrand ? `Edit brand: ${selectedBrand.brandName}` : 'Create new brand'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Brand Name"
                  required
                  value={editFormData.brandName}
                  onChange={(e) => handleEditFormChange('brandName', e.target.value)}
                  variant="outlined"
                  size="small"
                  error={!editFormData.brandName}
                  helperText={!editFormData.brandName ? 'Brand name is required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Manufacturer"
                  required
                  value={editFormData.manufacturer}
                  onChange={(e) => handleEditFormChange('manufacturer', e.target.value)}
                  variant="outlined"
                  size="small"
                  error={!editFormData.manufacturer}
                  helperText={!editFormData.manufacturer ? 'Manufacturer is required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={editFormData.country}
                  onChange={(e) => handleEditFormChange('country', e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Established Year"
                  value={editFormData.establishedYear}
                  onChange={(e) => handleEditFormChange('establishedYear', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., 2000"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Specialization"
                  value={editFormData.specialization}
                  onChange={(e) => handleEditFormChange('specialization', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., Solar Panels"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={editFormData.website}
                  onChange={(e) => handleEditFormChange('website', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="www.example.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => handleEditFormChange('email', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="info@example.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editFormData.phone}
                  onChange={(e) => handleEditFormChange('phone', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="+1-234-567-8900"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={editFormData.address}
                  onChange={(e) => handleEditFormChange('address', e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Certifications"
                  value={editFormData.certifications}
                  onChange={(e) => handleEditFormChange('certifications', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., ISO 9001, IEC 61215"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editFormData.description}
                  onChange={(e) => handleEditFormChange('description', e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.isActive}
                      onChange={(e) => handleEditFormChange('isActive', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Active Brand"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<Save />}
            sx={{ 
              backgroundColor: selectedBrand ? '#ff9800' : '#536DFE',
              '&:hover': { backgroundColor: selectedBrand ? '#f57c00' : '#3F51B5' }
            }}
          >
            {selectedBrand ? 'Save Changes' : 'Create Brand'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Brand;
